import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Flex,
  Image,
  Text,
  IconButton,
  Link as ChakraLink,
  VStack,
  HStack,
  Badge,
  useBreakpointValue,
  Icon,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin } from "lucide-react";
import linkedin from "../images/linkedin.png";
import saniyaPhoto from "../images/saniyasidiqui.jpg";
import shreyaPhoto from "../images/shreyashah.jpg";
import aadityaPhoto from "../images/adityasawant.jpg";
import sharvariPhoto from "../images/shravaniupdated.jpg";
import vidhulaPhoto from "../images/sdvidhula.jpg";
import akashPhoto from "../images/akashkedari.jpg";
import shreyashPhoto from "../images/shreyasdhamane.jpg";
import rohitPhoto from "../images/rohitkumarsaha.jpg";
import gayatriPhoto from "../images/gayatridhond.jpg";
import harshPhoto from "../images/harshzaveri.jpg";
import tejalPhoto from "../images/tejalkomb.jpg";
import mugdhaPhoto from "../images/mugdhakangane.jpg";
import mahadevPhoto from "../images/madhavbhalla.jpg";
import ameyPhoto from "../images/ameyagarwal.jpg";
import shraddhaPhoto from "../images/shraddhabhagyavant.jpg";
import adishPhoto from "../images/adishpadalia.jpg";
import aryaPhoto from "../images/aryagawde.jpg";
import rajasPhoto from "../images/rajjasbhope.jpg";
import sanchiPhoto from "../images/sanchikhopkar.jpg";
import siddhiBPhoto from "../images/siddhibalsaraf.jpg";
import vinishaPhoto from "../images/vinishabhagwani.jpg";
import vanshPhoto from "../images/vanshtalwar.jpg";
import priyaPhoto from "../images/priyavishwakarma.jpg";
import vrushteePhoto from "../images/vrushteejasani.jpg";
import urshitaPhoto from "../images/urshitakoshti.jpg";
import deeptiPhoto from "../images/deeptimudartha.jpg";
import kevalPhoto from "../images/kevaldhanani.jpg";
import sashikantPhoto from "../images/shashikantyadav.jpg";
import soumyaPhoto from "../images/soumyakoppaka.jpg";
import samiraPhoto from "../images/samiradeshpande.jpg";


import siddhiPlaceholder from "../images/avatars/avatar2.png";
import davidPlaceholder from "../images/avatars/avatar5.png";
import bhavikPlaceholder from "../images/avatars/avatar11.png";
import purvaPlaceholder from "../images/avatars/avatar1.png";
import eileenPlaceholder from "../images/avatars/avatar9.png";
import yashPlaceholder from "../images/avatars/avatar10.png";
import sourajaPlaceholder from "../images/avatars/avatar11.png";
import romilPlaceholder from "../images/avatars/avatar1.png";
import sarveshPlaceholder from "../images/avatars/avatar2.png";
import sarahPlaceholder from "../images/avatars/avatar3.png";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);

const PrevInterns = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const isMobile = useBreakpointValue({ base: true, md: false });

  const data = [
    { srNo: 1, name: "Saniya Siddiqui", previousRole: "Clinical Research Intern", role: "Clinical Researcher", company: "Skinzy / AiM4u", review: "I worked on Dermalens skin disease detection, collected data from KEM hospital for diabetic foot care, and managed Autobuddys LinkedIn posts. This internship helped me gain good experience in clinical research and explore new areas of work, providing more clarity in my career and confidence. Dr. Dhananjay Kalbande Sir helped me understand the workplace environment and what is expected from an individual, keeping us motivated and guided throughout.", imageSrc: saniyaPhoto, linkedin: "https://www.linkedin.com/in/saniya-siddiqui-a1ab8a331" },

    { srNo: 2, name: "Siddhi Sunil Patankar", previousRole: "Clinical Research Intern", role: "Clinical Researcher", company: "Skinzy / AiM4u", review: "I was involved in the collection and validation of data from diabetic patients, which helped me understand the importance of accurate data in research. Working on the DermaLens app gave me exposure to technology for early screening. This experience strengthened my sense of responsibility, teamwork, and interest in healthcare innovation. Dr. Dhananjay Kalbande Sir was very supportive and approachable, providing clear guidance and feedback that made the learning process smooth.", imageSrc: siddhiPlaceholder, linkedin: "#" },

    { srNo: 3, name: "Shreya Shah", previousRole: "Research Intern", role: "Dementia Research Intern", company: "Skinzy / AiM4u", review: "I am analyzing speech and language data for early signs of cognitive decline in a dementia research project. This strengthened my analytical skills and gave me clarity on pursuing higher studies in research-oriented fields. Dr. Dhananjay Kalbande Sir has been extremely supportive, sharing real-world insights beyond theory. His constant encouragement to think independently and work on new ideas motivated me to explore more and grow with confidence.", imageSrc: shreyaPhoto, linkedin: "https://www.linkedin.com/in/shreya-shah-763705285" },

    { srNo: 4, name: "Aaditya Sawant", previousRole: "ML Intern", role: "Machine Learning Intern", company: "Skinzy / AiM4u", review: "Worked under the AI4Climate initiative as an ML intern, where I developed my first ML models: an NLP-Deep Learning model for plant-based skin care recommendations and a CNN to classify tree bark. This experience helped me figure out my interest in the math behind machine learning and laid the foundation for me to secure an internship at IIT Bombay. Dr. Dhananjay Kalbande Sir's guidance was a blessing; through his constant motivation, I was able to submit an IEEE transaction paper. He supported me more like a friend and well-wisher than a teacher, which truly upskilled me.", imageSrc: aadityaPhoto, linkedin: "https://www.linkedin.com/in/aaditya-sawant2005/" },

    { srNo: 5, name: "David Daniels", previousRole: "Backend Development Intern", role: "Backend Developer", company: "Skinzy / AiM4u", review: "I developed the Django-based backend for the SkinTara mobile app, including the database, API development, integration of ML models, and final deployment. This internship helped me truly understand how to build projects used at scale in the real world rather than just hobby projects. Sir's mentorship was invaluable in helping me deal with real-world teams, guiding my communication and teaching me how to function effectively in a true project environment.", imageSrc: davidPlaceholder, linkedin: "https://www.linkedin.com/in/david-daniels142/" },

    { srNo: 6, name: "Sharvari Birajdar", previousRole: "Flutter Developer", role: "Mobile App Developer", company: "Skinzy / AiM4u", review: "I built an educational game for the AutoBuddys App to make math fun and accessible for autistic children. The app was deployed on the Play Store, App Store, and the Govt. of India portal, ensuring performance and reliability across platforms. This experience significantly built my confidence, and I learned essential management skills through Dr. Dhananjay Kalbande Sir's leadership.", imageSrc: sharvariPhoto, linkedin: "https://www.linkedin.com/in/sharvari-birajdar" },

    { srNo: 7, name: "S D VIDHULA", previousRole: "Clinical Research Intern", role: "Clinical Researcher", company: "Skinzy / AiM4u", review: "I worked on the KEM Physiotherapy project, specifically the detection and annotation of diabetic foot care. This internship provided me with certifications and established clear career goals for my higher studies. Dr. Dhananjay Kalbande Sir provided guidance throughout the project whenever we faced difficulties; we learned many skills, techniques, and real-world insights that will be helpful in my future path.", imageSrc: vidhulaPhoto, linkedin: "https://www.linkedin.com/in/s-d-vidhula-775502343" },

    { srNo: 8, name: "Akash Kedari", previousRole: "Mobile Developer", role: "Mobile/Flutter Developer", company: "Skinzy / AiM4u", review: "I contributed to the Dermalens and AutoBuddys projects, which got me thoroughly acquainted with the basics of mobile development. Thanks to this valuable opportunity, I secured my first full-time job. Kalbande Sir was always helpful and motivating throughout the process, tackling every situation with positive intent.", imageSrc: akashPhoto, linkedin: "https://www.linkedin.com/in/akash-kedari-8ab390235" },

    { srNo: 9, name: "Shreyash Dhamane", previousRole: "SDE Intern", role: "Full Stack / Mobile Developer", company: "Skinzy / AiM4u", review: "I migrated the Skinzy mobile app to current Flutter versions, resolved Razorpay payment gateway issues, and hosted skin detection models on AWS Lambda. I also contributed to the Skinzy Quiz website, Autobuddy website/app, and three research papers. Dr. Kalabande Sir gave me the opportunity to work in AI/ML despite no prior experience and guided me through my higher-studies selection and application process. Technical skills, leadership roles, and the exposure to industry technologies were pivotal for my career growth.", imageSrc: shreyashPhoto, linkedin: "https://www.linkedin.com/in/shreyash-dhamane-006671209/" },

    { srNo: 10, name: "Rohit Kumar Saha", previousRole: "Research Intern", role: "Clinical Research Intern", company: "Skinzy / AiM4u", review: "I gained hands-on experience in gathering accurate data from diabetic patients (blood glucose, lifestyle, medication), data annotation (labeling Type 1/Type 2/high-risk), and medical data validation. This enhanced my technical skills, work discipline, and preparation for future opportunities in data science and AI. Dr. Dhananjay Kalbande Sir’s explanations helped bridge the gap between theoretical concepts and real-world clinical applications, significantly strengthening my foundational knowledge.", imageSrc: rohitPhoto, linkedin: "https://www.linkedin.com/in/rohit-kumar-saha-0b1a47321" },

    { srNo: 11, name: "Bhavik Bhatia", previousRole: "Backend Development Intern", role: "Backend Developer", company: "Skinzy / AiM4u", review: "I worked as a Backend Developer on the Gyan platform, developing services using Django and REST APIs, implementing role-based access control, and managing MySQL databases. I integrated PhonePe, email notifications, and resume uploads while collaborating with the frontend team. This bridged the gap between theory and practical system design, helping me secure my next role at Russells Investments. Dr. Dhananjay Kalbande Sir’s mentorship ensured concepts were truly understood, strengthening my technical competence and professional mindset.", imageSrc: bhavikPlaceholder, linkedin: "https://www.linkedin.com/in/bhavik-bhatia-275b5420b" },

    { srNo: 12, name: "Gayatri Anil Dhond", previousRole: "Clinical Research Intern", role: "Clinical Researcher", company: "Skinzy / AiM4u", review: "I was exposed to data annotation and structured record-keeping while interacting with diabetic patients to create awareness about foot care. This internship significantly boosted my confidence in clinical communication and encouraged practical problem-solving. Dr. Dhananjay Kalbande Sir constantly pushed me out of my comfort zone, and his optimistic approach boosted our entire team’s confidence, helping us approach challenges with a fresh perspective.", imageSrc: gayatriPhoto, linkedin: "https://www.linkedin.com/in/gayatri-dhond-469b5122a" },

    { srNo: 13, name: "Purva Khot", previousRole: "Research Intern", role: "Biotechnology Research Intern", company: "Skinzy / AiM4u", review: "I worked on the KEM–Physiotherapy project for diabetic detection through foot images, gaining confidence in interacting with patients and medical professionals. As a biotechnology student, it showed me how to work at the intersection of healthcare and technology. Sir’s mentorship and passion for interdisciplinary work was motivating, revealing new ways technology can be applied to healthcare that I had never heard of before.", imageSrc: purvaPlaceholder, linkedin: "https://www.linkedin.com/in/purvakhot14" },

    { srNo: 14, name: "Harsh Zaveri", previousRole: "Flutter Developer Intern", role: "Mobile App Developer", company: "Skinzy / AiM4u", review: "I developed a mobile application for the Diabetic Foot project using Flutter from scratch, implementing a full authentication flow, complex questionnaires, and exercise/nutrition modules. This helped me apply theoretical mobile development concepts to a real-world healthcare application and improved my problem-solving skills. Dr. Dhananjay Kalbande Sir’s high expectations and constructive feedback pushed me to deliver quality work and developed my professional approach toward software development.", imageSrc: harshPhoto, linkedin: "https://in.linkedin.com/in/harsh-zaveri54" },

    { srNo: 15, name: "Tejal Komb", previousRole: "Full-stack Developer", role: "Full Stack Developer", company: "Skinzy / AiM4u", review: "I redesigned the Gyan website UI using React.js, deployed backends on AWS EC2, and integrated Google AdSense for monetization across multiple platforms like AutoBuddys and AI4Climate. Managing live applications and resolving complex deployment issues helped me become industry-ready and adaptable. Sir’s constant support and belief in my abilities made this a meaningful experience, helping me clearly apply technical concepts to real projects.", imageSrc: tejalPhoto, linkedin: "https://www.linkedin.com/in/tejal-komb-913555254" },

    { srNo: 16, name: "Mugdha Kangane", previousRole: "Flutter developer", role: "Mobile App Developer", company: "Skinzy / AiM4u", review: "During my internship, I worked on the DermaLens and Skin Tara applications, strengthening my skills in Flutter and Firebase. I gained hands-on experience in feature enhancements, backend integration, and troubleshooting server-related issues. This internship boosted my confidence in working on live projects and collaborating with a team. Dr. Dhananjay Kalbande Sir’s continuous guidance and constructive feedback helped me improve technically and professionally, providing clear explanations of concepts and real-world insights.", imageSrc: mugdhaPhoto, linkedin: "https://www.linkedin.com/in/mugdhakangane" },

    { srNo: 17, name: "Mahadev Balla", previousRole: "Backend Development Intern", role: "Backend Developer", company: "Skinzy / AiM4u", review: "I worked on backend development for the SkinTara and TechForSocial projects, building and maintaining APIs using Django and PostgreSQL. I handled database design, user management, and features like automated reminders. The internship provided valuable hands-on experience on live systems and Django-based application design. Dr. Kalbande provided high-level direction, framing how our technical work connects to real-world social impact and broader project goals.", imageSrc: mahadevPhoto, linkedin: "https://www.linkedin.com/in/mahadev-balla/" },

    { srNo: 18, name: "Amey Agarwal", previousRole: "AI/ML Research Intern", role: "AI/ML Researcher", company: "Skinzy / AiM4u", review: "Under Kalbande sir's supervision, I worked on Skin disease classification, Plant Recognition, and the Diabetic Foot Care project using OpenCV and Tensorflow. I led the skin disease classification team and contributed to various research papers. This experience pivoted my work into the healthcare domain and propelled me toward higher studies. Kalbande sir teaches through experience; he encourages independent problem-solving while providing support through expert connections when needed, emphasizing the importance of teacher-student collaboration.", imageSrc: ameyPhoto, linkedin: "https://www.linkedin.com/in/amey-agarwal-5bb56224b/" },

    { srNo: 19, name: "Shraddha Bhagyawant", previousRole: "UI/UX Designer", role: "Product Designer", company: "Skinzy / AiM4u", review: "I worked end-to-end on the design process for the AutoBuddys project, from logo creation to user research and interactive simulations for autistic children. This internship helped bridge the gap between academic learning and real-world application, boosting my confidence in design decision-making. Dr. Dhananjay Kalbande Sir’s mentorship on HMI and inclusive design was foundational, helping me refine my design thinking through constant feedback and real-world insights.", imageSrc: shraddhaPhoto, linkedin: "https://www.linkedin.com/in/shraddha-bhagyawant-083675238" },

    { srNo: 20, name: "Adish Manish Padalia", previousRole: "AI/ML Intern", role: "Full Stack / ML Developer", company: "Skinzy / AiM4u", review: "I developed machine learning experiments for AiM4u and worked as a Website/API developer at Skinzy using React.js and Flask. This internship strengthened my academic understanding by applying ML and full-stack concepts to real-world systems, boosting my confidence in deployment and version control. Dr. Dhananjay Kalbande Sir’s emphasis on clean implementation and research-oriented mindset provided clear technical guidance and deeper clarity into software development concepts.", imageSrc: adishPhoto, linkedin: "https://www.linkedin.com/in/adish-padalia-a3768a230/" },

    { srNo: 21, name: "Eileen Mascarenhas", previousRole: "Full stack development", role: "Full Stack Developer", company: "Skinzy / AiM4u", review: "I contributed to the RESTful API backend for the GYAN website, integrated payment gateways, and deployed the Django backend on AWS EC2 using Gunicorn and Nginx. I also designed responsive UI pages and implemented SEO best practices. This role gave me career clarity and independence in professional output. Dr. Dhananjay Kalbande Sir’s mentorship was invaluable, teaching us how to build at scale, handle professional responsibilities, and use personalized feedback for growth.", imageSrc: eileenPlaceholder, linkedin: "https://www.linkedin.com/in/eileenmas//" },

    { srNo: 22, name: "Yash Bhadane", previousRole: "Software Developer", role: "Software Developer", company: "Skinzy / AiM4u", review: "I worked on the IoT-Based Auto Buddy for Elderly Care, tracking vitals like heart rate and SpO₂ using sensors and ML models to predict blood pressure. This gave me hands-on experience in integrating hardware, software, and ML for practical healthcare solutions. Dr. Dhananjay Kalbande Sir’s mentorship was pivotal, deepening my understanding of real-world challenges and solidifying my passion for creating impactful tech solutions in healthcare.", imageSrc: yashPlaceholder, linkedin: "#" },

    { srNo: 23, name: "Souraja Mitra", previousRole: "Clinical Research Intern", role: "Clinical Researcher", company: "Skinzy / AiM4u", review: "I was introduced to the practical aspects of clinical research in a healthcare startup setting, gaining exposure to clinical workflows, patient data handling, and regulatory validation procedures. This helped bridge the gap between theory and real-world application, providing clarity for my career in healthcare innovation. Dr. Kalbande’s mentorship provided valuable insights into entrepreneurship and encouraged analytical thinking and a disciplined research approach.", imageSrc: sourajaPlaceholder, linkedin: "https://www.linkedin.com/in/souraja-mitra-1b38781a6" },

    { srNo: 24, name: "Arya Gawde", previousRole: "Machine Learning Intern", role: "Machine Learning Researcher", company: "Skinzy / AiM4u", review: "I developed automated computer vision pipelines using Mask R-CNN and MobileNet for skin disease classification and severity prediction, achieving 94% accuracy. This provided practical exposure to deep learning on real medical datasets and strengthened my knowledge of PyTorch. Dr. Kalbande Sir provided crucial technical guidance and motivation, helping me overcome conceptual difficulties and enhancing my understanding of real-world AI applications.", imageSrc: aryaPhoto, linkedin: "https://www.linkedin.com/in/arya-gawde-a855b4262" },

    { srNo: 25, name: "Romil Ashishkumar Shah", previousRole: "Flutter App Development Intern", role: "Mobile App Developer", company: "Skinzy / AiM4u", review: "I resolved critical bugs and upgraded the Dermalens application for iOS and Android, optimizing API architecture and ML model integration. I also handled global deployments on the Play Store and App Store. This experience strengthened my practical understanding of the full development lifecycle and production-level code. Dr. Dhananjay Kalbande Sir’s continuous guidance and real-world insights bridged the gap between academic concepts and industry practices, building my professional confidence.", imageSrc: romilPlaceholder, linkedin: "https://www.linkedin.com/in/romil-shah-7195ba230/" },

    { srNo: 26, name: "Rajas Bhope", previousRole: "AI/ML Intern", role: "AI/ML Researcher", company: "Skinzy / AiM4u", review: "I developed a predictive model to automate skin type classification leveraging the Baumann Skin Type Indicator (BSTI) framework. A major highlight was authoring and publishing a research paper under Dr. Kalbande’s mentorship. He provided the platform for high-level research using authentic datasets, and his guidance through the research and publication process was invaluable in my journey into Deep Learning and Computer Vision.", imageSrc: rajasPhoto, linkedin: "https://www.linkedin.com/in/rajas-bhope/" },

    { srNo: 27, name: "Sanchi Khopkar", previousRole: "Research Intern", role: "Clinical Research Intern", company: "Skinzy / AiM4u", review: "I worked on projects like Diabetic Foot Care and DermaLens, focusing on data collection, patient interaction, and early-stage healthcare application development. This helped me apply theoretical knowledge to real-world AI-based applications and improved my clarity regarding career goals. Dr. Dhananjay Kalbande Sir’s mentorship helped me develop a deeper understanding of research-oriented thinking and motivated me to approach real-world problems with a practical and ethical mindset.", imageSrc: sanchiPhoto, linkedin: "https://www.linkedin.com/in/sanchi-khopkar-923039311" },

    { srNo: 28, name: "Siddhi Balsaraf", previousRole: "Research intern", role: "Clinical Researcher", company: "Skinzy / AiM4u", review: "I worked on the Skinzy project, interacting with patients at KEM hospital to collect data and conduct analysis. This build my communication skills and showed me different ways to work in the medical field beyond a permanent lab setup. Dr. Dhananjay Kalbande Sir’s mentorship showed me how to relate my field with technology, leading to broader pathways for my career and a deeper understanding of health-tech innovation.", imageSrc: siddhiBPhoto, linkedin: "https://www.linkedin.com/in/siddhi-balsaraf-8b619325a" },

    { srNo: 29, name: "Vinisha Bhagwani", previousRole: "Research Intern", role: "Full Stack / Backend Intern", company: "Skinzy / AiM4u", review: "I gained hands-on experience with backend architecture and cloud deployment on AWS for the Gyan platform, and contributed to the resume management module. This enhanced my technical skills and problem-solving ability in production-level systems. Dr. Dhananjay Kalbande Sir’s mentorship was crucial; his guidance and timely feedback helped us identify errors and think practically, giving me deeper clarity and confidence in my work.", imageSrc: vinishaPhoto, linkedin: "https://www.linkedin.com/in/vinisha-bhagwani-8b619325b/" },

    { srNo: 30, name: "Vansh Talwar", previousRole: "Application Engineer and Business Developer", role: "SDE & Business Developer", company: "Skinzy / AiM4u", review: "I developed interactive educational games for autistic children using SwiftUI and Flutter, conducting on-ground user testing and publishing apps to global stores. This strengthened my end-to-end product ownership and enhanced my empathy and responsibility. Dr. Dhananjay Kalbande Sir’s emphasis on clarity of fundamentals and disciplined problem-solving helped bridge the gap between academic concepts and practical implementation, instilling a professional approach to project execution.", imageSrc: vanshPhoto, linkedin: "https://www.linkedin.com/in/vanshtalwar123?trk=contact-info" },

    { srNo: 31, name: "Priya Vishwakarma", previousRole: "Biotech Research Intern", role: "Biotech Researcher", company: "Skinzy / AiM4u", review: "My internship at Skinzy provided me with a fantastic opportunity to work on cutting-edge biotechnology projects. I was able to apply my biotech research skills to real-world problems and learned a great deal about the intersection of biology and healthcare technology. The mentorship I received was invaluable in shaping my professional growth.", imageSrc: priyaPhoto, linkedin: "https://www.linkedin.com/in/priya-vishwakarma-843150246/" },

    { srNo: 32, name: "Vrushtee Jasani", previousRole: "App Dev Intern", role: "App Developer", company: "Skinzy / AiM4u", review: "My internship at Skinzy provided me with a fantastic opportunity to work on building and optimizing mobile applications. I was able to apply my development skills to real-world problems and learned a great deal about creating user-centric solutions in healthcare technology. The mentorship I received was invaluable in shaping my professional growth.", imageSrc: vrushteePhoto, linkedin: "https://www.linkedin.com/in/vrushtee-jasani-288761285/" },

    { srNo: 33, name: "Urshita Koshti", previousRole: "Research Intern", role: "AI Research Intern", company: "Skinzy / AiM4u", review: "My internship at Skinzy provided me with a fantastic opportunity to work on cutting-edge AI projects. I was able to apply my research skills to real-world problems and learned a great deal about the intersection of technology and healthcare. The mentorship I received was invaluable in shaping my professional growth.", imageSrc: urshitaPhoto, linkedin: "https://www.linkedin.com/in/urshitakoshti/" },

    { srNo: 34, name: "Deepti Mudartha", previousRole: "ML Intern", role: "Machine Learning Intern", company: "Skinzy / AiM4u", review: "My internship at Skinzy provided me with a fantastic opportunity to work on complex machine learning models. I was able to apply my ML research skills to real-world datasets and learned a great deal about the practical implementation of AI in healthcare. The mentorship I received was invaluable in shaping my professional growth.", imageSrc: deeptiPhoto, linkedin: "https://www.linkedin.com/in/deepti-mudartha-042b30186/" },
    {
      srNo: 35,
      name: "Keval Dhanani",
      previousRole: "AI/ML Intern",
      role: "AI/ML Intern",
      company: "Skinzy / AiM4u",
      review: "Worked on computer vision and machine learning model experimentation, improving deep learning understanding and deployment skills. Received guidance in research direction and model optimization throughout the internship.",
      imageSrc: kevalPhoto,
      linkedin: "https://www.linkedin.com/in/keval-dhanani-01/"
    },

    {
      srNo: 36,
      name: "Sarvesh Deshpande",
      previousRole: "Backend Intern",
      role: "Backend Intern",
      company: "Skinzy / AiM4u",
      review: "Worked on Django APIs and database handling, gaining real-world backend development exposure. Mentorship helped build technical clarity and a structured coding approach.",
      imageSrc: sarveshPlaceholder,
    },

    {
      srNo: 37,
      name: "Shashikant Yadav",
      previousRole: "ML Intern",
      role: "ML Intern",
      company: "Skinzy / AiM4u",
      review: "Worked on model training and experimentation, strengthening machine learning fundamentals. Received research guidance that improved conceptual clarity and implementation skills.",
      imageSrc: sashikantPhoto,
      linkedin: "https://www.linkedin.com/in/shashikant-yadav-02/"
    },

    {
      srNo: 38,
      name: "Soumya Koppaka",
      previousRole: "Clinical Research Intern",
      role: "Clinical Research Intern",
      company: "Skinzy / AiM4u",
      review: "Contributed to patient data collection and annotation, enhancing clinical communication and structured research skills. Mentorship emphasized research discipline and practical healthcare application.",
      imageSrc: soumyaPhoto,
      linkedin: "https://www.linkedin.com/in/soumya-koppaka-700825194/"
    },

    {
      srNo: 39,
      name: "Samira Deshpande",
      previousRole: "Research Intern",
      role: "Research Intern",
      company: "Skinzy / AiM4u",
      review: "Worked on healthcare data handling and analysis, improving analytical clarity and applied research skills. Mentorship focused on strengthening conceptual understanding and real-world application.",
      imageSrc: samiraPhoto,
      linkedin: "https://www.linkedin.com/in/samira-deshpande-857401184/"
    },

    {
      srNo: 40,
      name: "Sarah Sonje",
      previousRole: "App Development Intern",
      role: "App Development Intern",
      company: "Skinzy / AiM4u",
      review: "Contributed to mobile application development, gaining exposure to production-level app workflows. Mentorship helped build professional discipline and practical implementation skills.",
      imageSrc: sarahPlaceholder,
      linkedin: "https://www.linkedin.com/in/sarah-sonje/"
    }

  ];

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + data.length) % data.length);
    setIsAutoPlaying(false);
  }, [data.length]);

  const handleNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % data.length);
    setIsAutoPlaying(false);
  }, [data.length]);

  // Auto-play slideshow
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % data.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, data.length]);

  const currentIntern = data[currentIndex];

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9,
    }),
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut",
      },
    }),
  };

  const pulseAnimation = {
    scale: [1, 1.05, 1],
    transition: { duration: 2, repeat: Infinity, ease: "easeInOut" }
  };

  return (
    <Box position="relative" overflow="hidden" py={{ base: 12, md: 20 }}>
      {/* ✨ Navy Blue Gradient Background */}
      <Box
        position="absolute"
        inset="0"
        bgGradient="linear(135deg, #0a1628 0%, #1a365d 25%, #0f3d93 50%, #0b3064 75%, #0a1628 100%)"
      />

      {/* ✨ Secondary Gradient Overlay */}
      <Box
        position="absolute"
        inset="0"
        bgGradient="radial(ellipse at 30% 20%, rgba(72, 187, 120, 0.15) 0%, transparent 50%)"
      />
      <Box
        position="absolute"
        inset="0"
        bgGradient="radial(ellipse at 70% 80%, rgba(49, 130, 206, 0.2) 0%, transparent 50%)"
      />

      {/* ✨ Dot Pattern */}
      <Box
        position="absolute"
        inset="0"
        opacity="0.1"
        pointerEvents="none"
        sx={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(72, 187, 120, 0.8) 1px, transparent 0)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* ✨ Animated Grid Lines */}
      <Box
        position="absolute"
        inset="0"
        opacity="0.05"
        pointerEvents="none"
        sx={{
          backgroundImage: `
            linear-gradient(rgba(72, 187, 120, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(72, 187, 120, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: "100px 100px",
        }}
      />

      {/* ✨ Floating Bubbles */}
      <MotionBox
        position="absolute"
        top="-80px"
        left="-80px"
        w="200px"
        h="200px"
        borderRadius="full"
        bgGradient="radial(circle, rgba(72, 187, 120, 0.2) 0%, transparent 70%)"
        animate={{ scale: [1, 1.3, 1], rotate: [0, 180, 360] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />
      <MotionBox
        position="absolute"
        bottom="-60px"
        right="-60px"
        w="180px"
        h="180px"
        borderRadius="full"
        bgGradient="radial(circle, rgba(49, 130, 206, 0.25) 0%, transparent 70%)"
        animate={{ scale: [1.2, 1, 1.2], rotate: [360, 180, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
      />
      <MotionBox
        position="absolute"
        top="20%"
        right="5%"
        w="100px"
        h="100px"
        borderRadius="full"
        bgGradient="radial(circle, rgba(72, 187, 120, 0.15) 0%, transparent 70%)"
        animate={{ y: [0, -40, 0], x: [0, 20, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <MotionBox
        position="absolute"
        top="60%"
        left="8%"
        w="60px"
        h="60px"
        borderRadius="full"
        bgGradient="radial(circle, rgba(49, 130, 206, 0.2) 0%, transparent 70%)"
        animate={{ y: [0, 30, 0], x: [0, -15, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* ✨ Animated Corner Accents */}
      <MotionBox
        position="absolute"
        top="10%"
        left="5%"
        w="2px"
        h="80px"
        bgGradient="linear(to-b, #48BB78, transparent)"
        animate={{ opacity: [0.3, 0.8, 0.3], height: ["60px", "100px", "60px"] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
      <MotionBox
        position="absolute"
        bottom="15%"
        right="8%"
        w="2px"
        h="80px"
        bgGradient="linear(to-t, #3182CE, transparent)"
        animate={{ opacity: [0.3, 0.8, 0.3], height: ["60px", "100px", "60px"] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      {/* Content Container */}
      <Container maxW="7xl" position="relative" zIndex="10">
        {/* Header Section */}
        <VStack spacing={4} mb={{ base: 10, md: 16 }} textAlign="center">
          <MotionBox
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
          >
          </MotionBox>

          <MotionText
            fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
            fontWeight="bold"
            color="white"
            lineHeight="1.1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Success Stories from Our
            <Text
              as="span"
              display="block"
              bgGradient="linear(to-r, #3dd97e, #38B2AC, #3182CE)"
              bgClip="text"
              sx={{
                animation: "gradientShift 3s ease infinite",
                backgroundSize: "200% 200%",
                "@keyframes gradientShift": {
                  "0%": { backgroundPosition: "0% 50%" },
                  "50%": { backgroundPosition: "100% 50%" },
                  "100%": { backgroundPosition: "0% 50%" },
                },
              }}
            >
              Previous Interns
            </Text>
          </MotionText>

          <MotionText
            fontSize={{ base: "md", md: "lg" }}
            color="whiteAlpha.800"
            maxW="2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Discover how our alumni have transformed their careers and are now making
            an impact at the world's leading technology companies.
          </MotionText>

          {/* Animated underline */}
          <MotionBox
            w="120px"
            h="3px"
            bgGradient="linear(to-r, #48BB78, #3182CE)"
            borderRadius="full"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
        </VStack>

        {/* Slideshow Section */}
        <Box position="relative" minH={{ base: "650px", md: "520px" }}>
          {/* Navigation Buttons */}
          <MotionBox
            position="absolute"
            left={{ base: 0, md: -6 }}
            top="50%"
            transform="translateY(-50%)"
            zIndex="20"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <IconButton
              icon={<ChevronLeftIcon boxSize={8} />}
              aria-label="Previous"
              onClick={handlePrev}
              bgGradient="linear(to-r, rgba(16, 216, 99, 0.84), rgba(2, 133, 255, 0.95))"
              color="white"
              borderRadius="full"
              size="lg"
              border="1px solid"
              borderColor="whiteAlpha.300"
              backdropFilter="blur(10px)"
              _hover={{
                bgGradient: "linear(to-r, rgba(16, 216, 99, 0.84), rgba(2, 133, 255, 0.95))",
                borderColor: "whiteAlpha.500"
              }}
              _active={{ transform: "scale(0.95)" }}
              transition="all 0.2s"
            />
          </MotionBox>

          <MotionBox
            position="absolute"
            right={{ base: 0, md: -6 }}
            top="50%"
            transform="translateY(-50%)"
            zIndex="20"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <IconButton
              icon={<ChevronRightIcon boxSize={8} />}
              aria-label="Next"
              onClick={handleNext}
              bgGradient="linear(to-r, rgba(22, 255, 45, 0.69), rgb(32, 147, 255))"
              color="white"
              borderRadius="full"
              size="lg"
              border="1px solid"
              borderColor="whiteAlpha.300"
              backdropFilter="blur(10px)"
              _hover={{
                bgGradient: "linear(to-r, rgba(16, 216, 99, 0.84), rgba(2, 133, 255, 0.95))",
                borderColor: "whiteAlpha.500"
              }}
              _active={{ transform: "scale(0.95)" }}
              transition="all 0.2s"
            />
          </MotionBox>

          {/* Main Slide Content */}
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <MotionFlex
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              direction={{ base: "column-reverse", md: "row" }}
              align="center"
              justify="center"
              gap={{ base: 8, md: 16 }}
              px={{ base: 12, md: 20 }}
              py={8}
            >
              {/* Left Side - Info */}
              <VStack
                flex="1"
                align={{ base: "center", md: "flex-start" }}
                spacing={6}
                textAlign={{ base: "center", md: "left" }}
              >
                {/* Previous Role Badge */}
                <MotionBox
                  custom={0}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Badge
                    bgGradient="linear(to-r, rgba(31, 70, 125, 0.93), rgba(54, 167, 107, 0.95))"
                    color="white"
                    px={5}
                    py={2}
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="bold"
                    letterSpacing="0.3px"
                    backdropFilter="blur(10px)"
                    boxShadow="0 4px 18px rgba(0,0,0,0.25)"
                  >
                    🎓 Previously: {currentIntern.previousRole}
                  </Badge>
                </MotionBox>


                {/* Name */}
                <MotionText
                  custom={1}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                  fontWeight="bold"
                  color="white"
                  lineHeight="1.1"
                  textShadow="0 2px 20px rgba(0,0,0,0.3)"
                >
                  {currentIntern.name}
                </MotionText>

                {/* Current Role */}
                <MotionBox
                  custom={2}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    color="whiteAlpha.900"
                    fontWeight="semibold"
                  >
                    {currentIntern.role}
                  </Text>
                  <HStack spacing={2} mt={2} justify={{ base: "center", md: "flex-start" }}>
                    <MotionBox
                      animate={pulseAnimation}
                    >
                      <Icon as={MapPin} boxSize={5} color="#48BB78" />
                    </MotionBox>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      bgGradient="linear(to-r, #48BB78, #38B2AC, #3182CE)"
                      bgClip="text"
                      fontWeight="bold"
                    >
                      {currentIntern.company}
                    </Text>
                  </HStack>
                </MotionBox>

                {/* Review Box */}
                <MotionBox
                  custom={3}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  bgGradient="linear(135deg, rgba(72, 187, 120, 0.1), rgba(49, 130, 206, 0.1))"
                  backdropFilter="blur(12px)"
                  borderRadius="2xl"
                  p={6}
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  position="relative"
                  maxW="500px"
                  overflow="hidden"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    bgGradient: "linear(to-r, #48BB78, #3182CE)",
                  }}
                >
                  <Text
                    fontSize="5xl"
                    bgGradient="linear(to-br, #48BB78, #3182CE)"
                    bgClip="text"
                    position="absolute"
                    top={1}
                    left={4}
                    fontFamily="Georgia, serif"
                    fontWeight="bold"
                  >
                    "
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="whiteAlpha.900"
                    fontStyle="italic"
                    lineHeight="1.8"
                    pt={6}
                  >
                    {currentIntern.review}
                  </Text>
                </MotionBox>

                {/* LinkedIn Button */}
                <MotionBox
                  custom={4}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ChakraLink
                    href={currentIntern.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    display="inline-flex"
                    alignItems="center"
                    gap={2}
                    bgGradient="linear(to-r, #1A365D, #2C5282, #38A169)"
                    color="white"
                    px={6}
                    py={3}
                    borderRadius="full"
                    fontWeight="semibold"
                    backdropFilter="blur(10px)"
                    transition="all 0.3s ease"
                    _hover={{
                      bgGradient: "linear(to-r, #153E75, #2B6CB0, #48BB78)",
                      textDecoration: "none",
                      boxShadow: "0 6px 24px rgba(56, 161, 105, 0.4)",
                    }}
                    _active={{
                      transform: "scale(0.97)",
                    }}
                  >
                    <Image boxSize="22px" src={linkedin} alt="LinkedIn" />
                    Connect on LinkedIn
                  </ChakraLink>
                </MotionBox>
              </VStack>

              {/* Right Side - Photo */}
              <MotionBox
                flex={{ base: "none", md: "0.6" }}
                position="relative"
                initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
              >
                {/* Animated Glow Ring */}
                <MotionBox
                  position="absolute"
                  inset="-8"
                  borderRadius="2xl"
                  border="2px solid"
                  borderColor="transparent"
                  bgGradient="linear(to-br, #48BB78, #3182CE)"
                  opacity="0.3"
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 1.02, 1]
                  }}
                  transition={{
                    rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                    scale: { duration: 3, repeat: Infinity, ease: "easeInOut" }
                  }}
                  sx={{
                    background: "linear-gradient(135deg, #48BB78, #3182CE)",
                    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    padding: "3px",
                  }}
                />

                {/* Glow Effect */}
                <Box
                  position="absolute"
                  inset="-6"
                  bgGradient="radial(circle, rgba(72, 187, 120, 0.4) 0%, rgba(49, 130, 206, 0.3) 50%, transparent 70%)"
                  borderRadius="2xl"
                  filter="blur(25px)"
                />

                {/* Photo Frame */}
                <Box
                  position="relative"
                  bgGradient="linear(135deg, rgba(72, 187, 120, 0.2), rgba(49, 130, 206, 0.2))"
                  borderRadius="2xl"
                  p={2}
                  border="2px solid"
                  borderColor="whiteAlpha.300"
                  overflow="hidden"
                  backdropFilter="blur(10px)"
                >
                  <Image
                    src={currentIntern.imageSrc}
                    alt={currentIntern.name}
                    boxSize={{ base: "220px", md: "300px", lg: "340px" }}
                    objectFit="cover"
                    borderRadius="xl"
                  />

                  {/* Shine Effect */}
                  <Box
                    position="absolute"
                    top="0"
                    left="-100%"
                    w="50%"
                    h="100%"
                    bgGradient="linear(to-r, transparent, whiteAlpha.400, transparent)"
                    transform="skewX(-20deg)"
                    animation="shine 4s infinite"
                    sx={{
                      "@keyframes shine": {
                        "0%": { left: "-100%" },
                        "50%, 100%": { left: "200%" },
                      },
                    }}
                  />

                  {/* Corner Accent */}
                  <Box
                    position="absolute"
                    top={2}
                    right={2}
                    w="30px"
                    h="30px"
                    borderTop="3px solid"
                    borderRight="3px solid"
                    borderColor="#48BB78"
                    borderRadius="0 8px 0 0"
                  />
                  <Box
                    position="absolute"
                    bottom={2}
                    left={2}
                    w="30px"
                    h="30px"
                    borderBottom="3px solid"
                    borderLeft="3px solid"
                    borderColor="#3182CE"
                    borderRadius="0 0 0 8px"
                  />
                </Box>
              </MotionBox>
            </MotionFlex>
          </AnimatePresence>

          {/* Slide Indicators */}
          <HStack
            justify="center"
            spacing={2}
            mt={8}
            position="absolute"
            bottom={0}
            left="50%"
            transform="translateX(-50%)"
          >
            {data.map((_, index) => (
              <MotionBox
                key={index}
                w={currentIndex === index ? "28px" : "10px"}
                h="10px"
                borderRadius="full"
                bgGradient={currentIndex === index
                  ? "linear(to-r, #48BB78, #3182CE)"
                  : "none"
                }
                bg={currentIndex !== index ? "whiteAlpha.400" : undefined}
                cursor="pointer"
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                  setIsAutoPlaying(false);
                }}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                transition={{ duration: 0.2 }}
              />
            ))}
          </HStack>
        </Box>

        {/* Legacy Section */}
        <MotionBox
          mt={{ base: 16, md: 24 }}
          textAlign="center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Decorative Line */}
          <Box position="relative" mb={10}>
            <Box
              h="1px"
              bgGradient="linear(to-r, transparent, whiteAlpha.300, transparent)"
            />
            <MotionBox
              position="absolute"
              left="50%"
              top="50%"
              transform="translate(-50%, -50%)"
              w="60px"
              h="3px"
              bgGradient="linear(to-r, #48BB78, #3182CE)"
              borderRadius="full"
              animate={{ scaleX: [1, 1.5, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
          </Box>

          <Text
            fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
            color="whiteAlpha.900"
            fontWeight="medium"
            mb={4}
          >
            We have shaped a legacy of
            <Text
              as="span"
              bgGradient="linear(to-r, #48BB78, #38B2AC, #3182CE)"
              bgClip="text"
              fontWeight="bold"
            >
              {" "}excellence{" "}
            </Text>
            and aspire to shape many more.
          </Text>
          <Text
            fontSize={{ base: "md", md: "lg" }}
            color="whiteAlpha.600"
            maxW="xl"
            mx="auto"
          >
            Join our program and become the next success story.
          </Text>

          {/* Animated dots */}
          <HStack justify="center" spacing={3} mt={6}>
            {[0, 1, 2].map((i) => (
              <MotionBox
                key={i}
                w="8px"
                h="8px"
                borderRadius="full"
                bgGradient="linear(to-r, #48BB78, #3182CE)"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2,
                  ease: "easeInOut"
                }}
              />
            ))}
          </HStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default PrevInterns;